import { useEffect, useState } from "react";
import {
  Truck, Calculator, CurrencyDollar, Sparkle, CheckCircle, ArrowRight,
} from "@phosphor-icons/react";

/**
 * HeroWorkflowLoop — ambient scaled-down auto-cycling preview of the 3-step
 * workflow widget that lives on the landing page. Lives in the hero right
 * column where a static truck image used to be. Non-interactive, decorative.
 *
 * Cycles Add-Load → Margin-Breakdown → Payroll every ~2.8s so a returning
 * visitor sees the payoff before they even scroll.
 */
const CYCLE_MS = 2800;

/**
 * useCountUp — animates an integer from 0 → target over `duration` ms whenever
 * `active` flips true. Used to make the "Net margin" and "Take-home" numbers
 * feel LIVE (Emmy's point: a demo that just sits there looks like a screenshot;
 * a demo where the total ticks up looks like the software is actually running).
 */
function useCountUp(target, active, duration = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) { setValue(0); return; }
    const start = performance.now();
    let raf = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      // Ease-out cubic so the count-up decelerates naturally near the end
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return value;
}

export default function HeroWorkflowLoop() {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % 3), CYCLE_MS);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      data-testid="hero-workflow-loop"
      className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[4/3] sm:aspect-[5/4] bg-gradient-to-br from-[#0A0A0A] via-[#0A0A0A] to-[#1a0a05]"
      aria-hidden="true"
    >
      {/* Ambient orange glow */}
      <div
        className="absolute inset-0 opacity-60 pointer-events-none"
        style={{
          background:
            "radial-gradient(50% 45% at 50% 35%, rgba(255,90,31,0.16) 0%, rgba(255,90,31,0) 70%)",
        }}
      />

      {/* Stepper indicator */}
      <div className="absolute top-4 left-4 right-4 flex items-center gap-1.5 z-10">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
              i === step
                ? "bg-[#FF5A1F]"
                : i < step
                ? "bg-[#FF5A1F]/50"
                : "bg-white/10"
            }`}
          />
        ))}
      </div>

      {/* Frame wrapper — one card at a time, fades between */}
      <div className="absolute inset-0 flex items-center justify-center p-6 sm:p-8">
        <FrameAddLoad active={step === 0} />
        <FrameBreakdown active={step === 1} />
        <FramePayroll active={step === 2} />
      </div>

      {/* Bottom live-badge */}
      <div className="absolute bottom-3 left-4 right-4 flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 z-10">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
        Automations running live — 24/7
        <ArrowRight size={11} weight="bold" className="ml-auto text-[#FF5A1F]" />
      </div>
    </div>
  );
}

/* ─────────── Frames — absolutely positioned, cross-fade via opacity ─────────── */

function FrameAddLoad({ active }) {
  return (
    <div
      className={`absolute inset-x-6 inset-y-9 sm:inset-x-8 sm:inset-y-10 rounded-xl border border-white/10 bg-black/60 backdrop-blur-sm p-4 sm:p-5 flex flex-col transition-opacity duration-700 ${
        active ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF9764] mb-3">
        <Truck size={12} weight="bold" /> Step 1 · New broker load
      </div>
      <div className="grid grid-cols-2 gap-2 text-[11px]">
        <MiniCell label="Pickup" value="Chicago, IL" />
        <MiniCell label="Delivery" value="Dallas, TX" />
        <MiniCell label="Miles" value="950" mono />
        <MiniCell label="Rate" value="$2,800" mono accent />
      </div>
      <div className="mt-auto pt-3 flex items-center justify-between text-[10px]">
        <span className="text-neutral-500">Dry Van · 45k lbs</span>
        <span className="text-[#FF9764] inline-flex items-center gap-1">
          <Sparkle size={10} weight="fill" /> AI thinking…
        </span>
      </div>
    </div>
  );
}

function FrameBreakdown({ active }) {
  const netMargin = useCountUp(1319, active);
  return (
    <div
      className={`absolute inset-x-6 inset-y-9 sm:inset-x-8 sm:inset-y-10 rounded-xl border border-white/10 bg-black/60 backdrop-blur-sm p-4 sm:p-5 flex flex-col transition-opacity duration-700 ${
        active ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF9764] mb-3">
        <Calculator size={12} weight="bold" /> Step 2 · Margin breakdown
      </div>
      <ul className="space-y-1 font-mono text-[11px] flex-1">
        <LineRow label="Broker rate" value="$2,800" positive />
        <LineRow label="Fuel" value="− $592" />
        <LineRow label="Driver pay" value="− $523" />
        <LineRow label="Maintenance" value="− $143" />
        <LineRow label="Factoring + dispatch" value="− $224" />
      </ul>
      <div className="mt-2 rounded-lg border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-3 py-2 flex items-center justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[#FF9764]">Net margin</div>
          <div
            className="font-display text-xl font-semibold text-white tabular-nums"
            data-testid="hero-net-margin"
          >
            ${netMargin.toLocaleString()}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Verdict</div>
          <div className="text-[11px] text-emerald-300 font-semibold inline-flex items-center gap-1">
            <CheckCircle size={10} weight="fill" /> Accept
          </div>
        </div>
      </div>
    </div>
  );
}

function FramePayroll({ active }) {
  const takeHome = useCountUp(438, active);
  return (
    <div
      className={`absolute inset-x-6 inset-y-9 sm:inset-x-8 sm:inset-y-10 rounded-xl border border-white/10 bg-black/60 backdrop-blur-sm p-4 sm:p-5 flex flex-col transition-opacity duration-700 ${
        active ? "opacity-100" : "opacity-0"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#FF9764] mb-3">
        <CurrencyDollar size={12} weight="bold" /> Step 3 · Driver payroll
      </div>
      <ul className="space-y-1 font-mono text-[11px] flex-1">
        <LineRow label="KC → Chicago · 510 mi" value="$281" positive />
        <LineRow label="Chicago → Milwaukee · 92 mi" value="$51" positive />
        <LineRow label="Chicago → Dallas · 950 mi" value="$523" positive />
        <LineRow label="Fuel advance" value="− $285" />
        <LineRow label="Taxes withheld" value="− $131" />
      </ul>
      <div className="mt-2 rounded-lg border border-emerald-400/40 bg-emerald-400/10 px-3 py-2 flex items-center justify-between">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-emerald-300">Net take-home</div>
          <div
            className="font-display text-xl font-semibold text-white tabular-nums"
            data-testid="hero-take-home"
          >
            ${takeHome.toLocaleString()}
          </div>
        </div>
        <div className="text-right text-[10px] text-neutral-400">
          ACH triggered<br />
          <span className="text-neutral-500">Fri 6:00 AM</span>
        </div>
      </div>
    </div>
  );
}

function MiniCell({ label, value, mono, accent }) {
  return (
    <div className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className={`text-[11px] ${mono ? "font-mono" : ""} ${accent ? "text-[#FF9764] font-semibold" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}

function LineRow({ label, value, positive }) {
  return (
    <li className="flex items-baseline justify-between border-b border-white/5 pb-1">
      <span className="text-neutral-400 truncate pr-2">{label}</span>
      <span className={positive ? "text-emerald-300" : "text-neutral-300"}>{value}</span>
    </li>
  );
}
